import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const IDLE_LIMIT = 60 * 60 * 1000;
const WARN_BEFORE = 2 * 60 * 1000;
const LAST_KEY = "mixa_last_activity";
const touch = () => localStorage.setItem(LAST_KEY, String(Date.now()));
const idleFor = () => Date.now() - Number(localStorage.getItem(LAST_KEY) || Date.now());
const idleTooLong = () => idleFor() > IDLE_LIMIT;
export const idleRemaining = () => Math.max(0, IDLE_LIMIT - idleFor());

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [favIds, setFavIds] = useState([]);
  const [idleWarn, setIdleWarn] = useState(false);
  const warnRef = useRef(false);

  const loadFavs = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites/ids");
      setFavIds(data);
    } catch { setFavIds([]); }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadFavs();
    } catch {
      setUser(false);
    }
  }, [loadFavs]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { return; }
    refresh();
  }, [refresh]);

  const persist = (data) => {
    if (data?.token) localStorage.setItem("mixa_token", data.token);
    touch();
    setUser(data.user);
    loadFavs();
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return persist(data);
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return persist(data);
  };
  const googleSession = async (session_id) => {
    const { data } = await api.post("/auth/session", { session_id });
    return persist(data);
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("mixa_token");
    setUser(false);
    setFavIds([]);
  };

  useEffect(() => {
    if (!user) { return undefined; }
    if (idleTooLong()) {
      logout();
      toast.info("Vous avez été déconnecté après 1 heure d'inactivité.");
      return undefined;
    }
    touch();
    warnRef.current = false; setIdleWarn(false);
    let last = 0;
    const onActivity = () => {
      if (warnRef.current) { return; }
      const n = Date.now(); if (n - last > 15000) { last = n; touch(); }
    };
    const check = () => {
      if (idleTooLong()) {
        warnRef.current = false; setIdleWarn(false);
        logout();
        toast.info("Vous avez été déconnecté après 1 heure d'inactivité.");
        return;
      }
      const w = idleFor() > IDLE_LIMIT - WARN_BEFORE;
      warnRef.current = w; setIdleWarn(w);
    };
    const evs = ["click", "keydown", "scroll", "touchstart", "mousemove"];
    evs.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    const timer = setInterval(check, 5000);
    return () => {
      evs.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user]);

  const stayConnected = () => { touch(); warnRef.current = false; setIdleWarn(false); };

  const toggleFavorite = async (type, item_id) => {
    const { data } = await api.post("/favorites", { type, item_id });
    setFavIds((prev) =>
      data.favorited
        ? [...prev, { type, item_id }]
        : prev.filter((f) => !(f.type === type && f.item_id === item_id))
    );
    return data.favorited;
  };
  const isFav = (type, item_id) => favIds.some((f) => f.type === type && f.item_id === item_id);

  return (
    <AuthContext.Provider value={{ user, login, register, googleSession, logout, refresh, toggleFavorite, isFav, favIds, idleWarn, stayConnected }}>
      {children}
    </AuthContext.Provider>
  );
}
